import React, { useState, useEffect } from 'react';
import { MdOutlineEmail, MdLockOutline, MdArrowForward, MdCheckBox, MdCheckCircle } from 'react-icons/md';

const ResetPassword = ({ history }) => {
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState('idle'); // idle | loading | success | error | invalid
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Read email and token from URL params
        const params = new URLSearchParams(window.location.search);
        const emailParam = params.get('email');
        const tokenParam = params.get('token');

        if (!emailParam || !tokenParam) {
            setStatus('invalid');
            return;
        }

        setEmail(emailParam);
        setToken(tokenParam);
    }, []);

    const validate = () => {
        const e = {};
        if (!password) e.password = 'Password is required.';
        else if (password.length < 6) e.password = 'Password must be at least 6 characters.';
        else if (!/[A-Z]/.test(password)) e.password = 'Password must contain at least one uppercase letter.';
        else if (!/[0-9]/.test(password)) e.password = 'Password must contain at least one number.';
        if (!confirmPassword) e.confirmPassword = 'Please confirm your password.';
        else if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match.';
        return e;
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setStatus('loading');

        try {
            const response = await fetch('api/Account/ResetPassword', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Email: email,
                    Token: token,
                    NewPassword: password,
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                setErrors({ api: result.errorMessage || 'Failed to reset password. The link may have expired.' });
                setStatus('idle');
                return;
            }

            setStatus('success');
        } catch (err) {
            console.error('ResetPassword error:', err);
            setErrors({ api: 'Something went wrong. Please try again.' });
            setStatus('idle');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    // Invalid link state
    if (status === 'invalid') {
        return (
            <div className="page-centered">
                <div className="card" style={{ width: '100%', maxWidth: '660px' }}>
                    <h4 className="text-h1" style={{ margin: '0 0 var(--space-sm)' }}>
                        Invalid link
                    </h4>
                    <p className="signup-section__desc" style={{ margin: '0 0 var(--space-lg)' }}>
                        This password reset link is invalid or has expired. Please request
                        a new one.
                    </p>
                    <button
                        className="btn"
                        onClick={() => history.push('/forgot-password')}
                        style={{ width: '100%' }}
                    >
                        Request new link <MdArrowForward size={16} />
                    </button>
                </div>
            </div>
        );
    }

    // Success state
    if (status === 'success') {
        return (
            <div className="page-centered">
                <div className="card" style={{ width: '100%', maxWidth: '660px' }}>
                    <div className="eligibility-success">
                        <MdCheckCircle size={20} className="eligibility-success__icon" />
                        <div className="eligibility-success__text">
                            <strong>Password reset successfully</strong>
                            <p>Your password has been updated. You can now log in with your new password.</p>
                        </div>
                    </div>
                    <button
                        className="btn"
                        onClick={() => history.push('/login')}
                        style={{ width: '100%' }}
                    >
                        Back to login <MdArrowForward size={16} />
                    </button>

                    <footer className="signup-footer" style={{ marginTop: 'var(--space-lg)' }}>
                        <p>Need assistance?</p>
                        <p>Call us at <a href="tel:8772695422">877-269-5422</a> to connect.</p>
                    </footer>
                </div>
            </div>
        );
    }

    return (
        <div className="page-centered">
            <div className="card" style={{ width: '100%', maxWidth: '660px' }}>

                <div style={{ textAlign: 'left', marginBottom: 'var(--space-xl)' }}>
                    <h4 className="text-h1" style={{ margin: '0 0 var(--space-xs)' }}>
                        Enter new password
                    </h4>
                    <p className="text-default" style={{ margin: 0 }}>
                        Almost done! Choose a new password to complete your reset.
                    </p>
                </div>

                {/* Email - pre-filled and disabled */}
                <div className="form-group">
                    <label className="text-label">Email address</label>
                    <div className="input-icon-wrap">
                        <span className="input-icon">
                            <MdOutlineEmail size={16} />
                        </span>
                        <input
                            type="email"
                            value={email}
                            disabled
                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                        />
                    </div>
                </div>

                {/* New password */}
                <div className="form-group">
                    <label className="text-label">Please enter new password</label>
                    <p className="text-small" style={{ margin: '0 0 var(--space-xs)', color: 'var(--text-primary)', opacity: 0.6 }}>
                        Your new password must be at least 6 characters, include one
                        uppercase letter and one number.
                    </p>
                    <div className="input-icon-wrap">
                        <span className="input-icon">
                            <MdLockOutline size={16} />
                        </span>
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setErrors((prev) => ({ ...prev, password: null }));
                            }}
                            onKeyPress={handleKeyPress}
                            disabled={status === 'loading'}
                        />
                    </div>
                    {errors.password && (
                        <span className="error-text">{errors.password}</span>
                    )}
                </div>

                {/* Confirm password */}
                <div className="form-group">
                    <label className="text-label">Retype new password</label>
                    <div className="input-icon-wrap">
                        <span className="input-icon">
                            <MdLockOutline size={16} />
                        </span>
                        <input
                            type="password"
                            placeholder="Password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setErrors((prev) => ({ ...prev, confirmPassword: null }));
                            }}
                            onKeyPress={handleKeyPress}
                            disabled={status === 'loading'}
                        />
                    </div>
                    {errors.confirmPassword && (
                        <span className="error-text">{errors.confirmPassword}</span>
                    )}
                </div>

                {/* API error */}
                {errors.api && (
                    <p className="error-text" style={{ marginBottom: 'var(--space-md)' }}>
                        {errors.api}
                    </p>
                )}

                <div style={{ marginTop: 'var(--space-xs)' }}>
                    <button
                        className="btn"
                        onClick={handleSubmit}
                        disabled={status === 'loading'}
                        style={{ width: '100%' }}
                    >
                        {status === 'loading' ? 'Resetting…' : 'Reset password'}
                        {status !== 'loading' && <MdArrowForward size={16} />}
                    </button>
                </div>

                <p className="text-small" style={{ textAlign: 'center', margin: 'var(--space-md) 0 0' }}>
                    Don't have an account?{' '}
                    <a href="/signup/eligibility" className="auth-link"
                        style={{ fontWeight: 'var(--weight-semibold)' }}>
                        Get a quote
                    </a>{' '}
                    today.
                </p>

            </div>
        </div>
    );
};

export default ResetPassword;
